/**
 * CopyCampaignEntryPicker — in-workflow campaign browser shown by
 * CopyCampaignWorkflow when it's entered with zero preselected campaign ids
 * (CampaignsPage.js's toolbar "Copy campaign" button calls
 * `onStartCopyCampaign([])` deliberately — see that button's own title/doc
 * comment). Replaces the previous dead-end "go back and select campaigns"
 * message (Gap #4).
 *
 * Reuses GET /api/v2/campaigns with the EXACT param shape
 * CampaignsPage.js's own `fetchCampaigns` already sends (accountId, userId,
 * page, limit, sortBy, sortOrder, search) rather than inventing a second
 * listing call, and reuses CopyPickerStep.js's table/checkbox/search
 * copy-workflow.css classes (cw-ptoolbar/cw-psearch/cw-tbl-scroll/cb/
 * cw-picker-selbar/selcount) for visual consistency with the rest of this
 * workflow instead of a third distinct table style.
 *
 * Deliberately has NO selection state of its own that outlives this
 * component: on "Continue" it hands the chosen ids back up via
 * `onContinue(ids)` and CopyCampaignWorkflow.js feeds them straight into the
 * SAME `?campaignIds=` URL param (via setSearchParams) the rest of the flow
 * is already driven off of — see that call site's comment. Nothing
 * downstream needs to know (or ever will know) whether campaigns arrived via
 * URL preselection or in-workflow picking.
 */
import { useEffect, useState } from 'react';
import { api, getApiUrl } from '../../utils/apiConfig';
import { getPlatformName } from '../../utils/platformNames';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { copyIneligibilityReason } from './copyWorkflowApi';

export default function CopyCampaignEntryPicker({ accountId, userId, onContinue, onCancel }) {
  const [search, setSearch] = useState('');
  // Search-as-you-type against GET /api/v2/campaigns — debounced through the
  // shared typeahead hook so a burst of keystrokes fetches only the final term.
  const debouncedSearch = useDebouncedValue(search);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(getApiUrl('/api/v2/campaigns'), {
      params: {
        accountId,
        userId,
        page: 1,
        limit: 50,
        sortBy: 'created_at',
        sortOrder: 'desc',
        search: debouncedSearch,
      },
    })
      .then((response) => {
        if (cancelled) return;
        setCampaigns(response.data.campaigns || response.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching campaigns for the copy-campaign picker', err);
        setError(err.response?.data?.error || err.message || 'Failed to load campaigns');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accountId, userId, debouncedSearch]);

  // SINGLE SELECT — copy runs one campaign at a time (same rule as the
  // Campaigns table's checkbox column): picking a row replaces the selection,
  // re-picking it clears. A multi-campaign copy assumed ONE platform for the
  // whole batch, so mixing e.g. Search and PMax built against the wrong one.
  const toggle = (id) => {
    setSelectedIds((prev) => (prev.has(id) ? new Set() : new Set([id])));
  };

  return (
    <div className="cw-pane">
      <h1>Pick a campaign to copy</h1>
      <p className="lede">Search for the campaign you want to copy, select it, then continue.</p>
      <div className="cw-ptoolbar">
        <input
          className="cw-psearch"
          type="text"
          placeholder="Search campaigns…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="gap" />
        <span className="selnote">{selectedIds.size} selected</span>
      </div>
      {error && (
        <div className="gate-banner" style={{ margin: '0 22px 14px' }}>
          <span className="material-icons">error</span>
          <div className="t">{error}</div>
        </div>
      )}
      <div className="cw-tbl-scroll">
        <table>
          <thead>
            <tr>
              <th style={{ width: 34 }} />
              <th>Campaign</th><th>Platform</th><th>Status</th><th>Folder</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="muted">Loading campaigns…</td></tr>
            )}
            {!loading && campaigns.length === 0 && (
              <tr><td colSpan={5} className="muted">No campaigns match.</td></tr>
            )}
            {!loading && campaigns.map((c) => {
              const isOn = selectedIds.has(c.id);
              // Fail early: same eligibility gate as the Campaigns table's
              // checkbox column. An ineligible row (no channel / never pushed
              // to the platform / unsupported platform) renders locked with
              // the reason on its tooltip instead of failing as a build one
              // step later.
              const ineligibleReason = copyIneligibilityReason(c);
              if (ineligibleReason) {
                return (
                  <tr key={c.id} title={ineligibleReason} style={{ opacity: 0.45, cursor: 'not-allowed' }}>
                    <td><span className="cb lock" /></td>
                    <td><span className="nm"><b>{c.name}</b></span></td>
                    <td>{getPlatformName(c.destination)}</td>
                    <td>{c.status || '—'}</td>
                    <td className="muted">{c.folder_name || '—'}</td>
                  </tr>
                );
              }
              return (
                <tr key={c.id} className={isOn ? 'sel' : ''} onClick={() => toggle(c.id)}>
                  <td onClick={(e) => { e.stopPropagation(); toggle(c.id); }}>
                    <span className={`cb${isOn ? ' on' : ''}`} />
                  </td>
                  <td><span className="nm"><b>{c.name}</b></span></td>
                  <td>{getPlatformName(c.destination)}</td>
                  <td>{c.status || '—'}</td>
                  <td className="muted">{c.folder_name || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="cw-picker-selbar">
        <span className="selcount"><b>{selectedIds.size}</b> campaign selected</span>
        <div className="gap" />
        <button className="cw-btn cw-btn-secondary cw-btn-sm" onClick={onCancel}>Back to Campaigns</button>
        <button
          className="cw-btn cw-btn-primary cw-btn-sm"
          onClick={() => onContinue(Array.from(selectedIds))}
          disabled={selectedIds.size === 0}
        >
          <span className="material-icons">east</span> Continue
        </button>
      </div>
    </div>
  );
}
